/**
 * Alert Worthiness Picker Component (revamp T26)
 *
 * Three toggles over the user's alert-worthiness prefs ({stop, target,
 * catalyst}) — which briefing alerts reach their action items. Reads the
 * settings via useUserSettings and patches via useUpdateUserSettings; the
 * backend merges a partial object, but we always send the FULL merged map so
 * the intent is explicit on the wire.
 */

import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, Switch } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { showError } from '../../utils/alert';
import {
  useUserSettings,
  useUpdateUserSettings,
} from '../../services/api/queries/useUserSettings';
import type { AlertPrefs } from '../../services/userApi';

const PREF_KEYS: { key: keyof AlertPrefs; labelKey: string }[] = [
  { key: 'stop', labelKey: 'settings.alertStop' },
  { key: 'target', labelKey: 'settings.alertTarget' },
  { key: 'catalyst', labelKey: 'settings.alertCatalyst' },
];

const ALL_ON: AlertPrefs = { stop: true, target: true, catalyst: true };

export function AlertWorthinessPicker() {
  const { data: userSettings } = useUserSettings();
  const updateUserSettings = useUpdateUserSettings();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  // Optimistic toggle state, keyed per pref. Cleared back to the stored value
  // on failure so a rejected patch visibly snaps the switch off again.
  const [overrides, setOverrides] = useState<Partial<AlertPrefs>>({});

  const stored = userSettings?.alert_prefs;
  const valueFor = (key: keyof AlertPrefs) =>
    overrides[key] ?? stored?.[key] ?? ALL_ON[key];

  const handleToggle = async (key: keyof AlertPrefs, next: boolean) => {
    if (updateUserSettings.isPending || next === valueFor(key)) return;

    const merged: AlertPrefs = {
      stop: valueFor('stop'),
      target: valueFor('target'),
      catalyst: valueFor('catalyst'),
      [key]: next,
    };
    setOverrides((prev) => ({ ...prev, [key]: next }));
    try {
      // mutateAsync (not mutate): mutate() never rejects in React Query v5, so
      // awaiting it would make this catch dead (same rationale as
      // ProfileScreen's handlers).
      await updateUserSettings.mutateAsync({ alert_prefs: merged });
    } catch (error) {
      setOverrides((prev) => ({ ...prev, [key]: !next }));
      showError(
        error instanceof Error
          ? error.message
          : t('settings.alertUpdateFailed', {
              defaultValue: 'Failed to update alert settings',
            })
      );
    }
  };

  return (
    <View style={styles.sectionContainer}>
      <Text style={styles.sectionLabel}>{t('settings.alertWorthiness')}</Text>
      <Text style={styles.sectionDescription}>
        {t('settings.alertWorthinessDescription')}
      </Text>

      {PREF_KEYS.map(({ key, labelKey }) => (
        <View key={key} style={styles.row}>
          <Text style={[styles.rowLabel, { color: theme.colors.text }]}>
            {t(labelKey)}
          </Text>
          <Switch
            testID={`alert-pref-${key}`}
            value={valueFor(key)}
            onValueChange={(next: boolean) => handleToggle(key, next)}
            disabled={updateUserSettings.isPending}
            trackColor={{ false: theme.colors.border, true: theme.colors.primary }}
            thumbColor={theme.colors.backgroundElevated}
            accessibilityLabel={t(labelKey)}
            accessibilityRole="switch"
          />
        </View>
      ))}
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    sectionContainer: {
      backgroundColor: theme.colors.surfaceSecondary,
      borderRadius: theme.radii.lg,
      padding: theme.spacing.lg,
      marginBottom: theme.spacing['2xl'],
      borderWidth: 2,
      borderColor: theme.colors.border,
    },
    sectionLabel: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightSemibold,
      color: theme.colors.text,
      marginBottom: theme.spacing.xs,
    },
    sectionDescription: {
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.textSecondary,
      marginBottom: theme.spacing.md,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: theme.spacing.xs,
    },
    rowLabel: {
      fontSize: theme.typography.fontSizeMd,
      flexShrink: 1,
    },
  });
}
